import React, { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  CheckCircle,
  XCircle,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  User,
  Lightbulb,
  Heart,
  Star,
  Eye,
  Sparkles,
  Award,
  Zap,
  BarChart,
  MessageCircle,
  Feather,
  Users,
} from "lucide-react";
import OriginalResponsesDisplay from "./OriginalResponsesDisplay";
import ScaleBar from "./ScaleBar";

const PersonalityCard = ({
  trait,
  value,
  icon: Icon,
  index,
  isExpanded,
  onToggle,
  palette,
  questionTitles = {},
}) => {
  // Different background colors for visual variety
  const bgGradients = [
    "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)",
    "linear-gradient(135deg, rgba(var(--primary-color-rgb), 0.12) 0%, rgba(var(--primary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--secondary-color-rgb), 0.12) 0%, rgba(var(--secondary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--accent-color-rgb), 0.12) 0%, rgba(var(--accent-color-rgb), 0.06) 100%)",
  ];

  const gradient = bgGradients[index % bgGradients.length];

  // Detect if value is an array and properly format it
  const parseArrayValue = (val) => {
    if (Array.isArray(val)) {
      return val;
    }

    // Check if it's a stringified array
    if (typeof val === "string" && val.startsWith("[") && val.endsWith("]")) {
      try {
        return JSON.parse(val);
      } catch (e) {
        return null;
      }
    }

    return null;
  };

  // Helper to get left/right labels for scale
  const getScaleLabels = (trait, value) => {
    if (
      typeof value === "object" &&
      value !== null &&
      (value.leftLabel || value.rightLabel)
    ) {
      return {
        leftLabel: value.leftLabel || "",
        rightLabel: value.rightLabel || "",
      };
    }
    const lowerTrait = trait.toLowerCase();
    if (lowerTrait.includes("formal") || lowerTrait.includes("casual")) {
      return { leftLabel: "Casual", rightLabel: "Formal" };
    }
    if (lowerTrait.includes("playful") || lowerTrait.includes("serious")) {
      return { leftLabel: "Serious", rightLabel: "Playful" };
    }
    if (lowerTrait.includes("modern") || lowerTrait.includes("traditional")) {
      return { leftLabel: "Traditional", rightLabel: "Modern" };
    }
    if (lowerTrait.includes("loud") || lowerTrait.includes("quiet")) {
      return { leftLabel: "Quiet", rightLabel: "Loud" };
    }
    if (lowerTrait.includes("bright") || lowerTrait.includes("dark")) {
      return { leftLabel: "Dark", rightLabel: "Bright" };
    }
    return { leftLabel: "Low", rightLabel: "High" };
  };

  // Handle different value types (string, number, boolean, object, or array)
  const renderValue = () => {
    if (value === undefined || value === null) {
      return "No information provided";
    }

    // Check if it's an array or can be parsed as an array
    const arrayValue = parseArrayValue(value);
    if (arrayValue) {
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {arrayValue.map((item, idx) => (
            <span
              key={idx}
              className="px-2 py-1 bg-white/10 rounded-full text-xs"
            >
              {typeof item === "object" && item !== null
                ? typeof item.value !== "undefined"
                  ? String(item.value)
                  : JSON.stringify(item)
                : String(item)}
            </span>
          ))}
        </div>
      );
    }

    if (typeof value === "boolean") {
      return value ? (
        <div className="flex items-center">
          <CheckCircle className="text-green-400 mr-2" size={16} />
          <span>Yes</span>
        </div>
      ) : (
        <div className="flex items-center">
          <XCircle className="text-red-400 mr-2" size={16} />
          <span>No</span>
        </div>
      );
    }

    // Check for scale objects FIRST (before numeric checks)
    if (typeof value === "object" && value !== null) {
      // Handle scale objects with value, leftLabel, rightLabel
      // Check if it has a value property (number or string that can be converted to number)
      if (typeof value.value !== "undefined") {
        const numValue = typeof value.value === "number" 
          ? value.value 
          : parseFloat(value.value);
        
        // If it's a valid scale number and has labels, treat as scale object
        if (!isNaN(numValue) && numValue >= 1 && numValue <= 10) {
          return (
            <div>
              <ScaleBar
                value={numValue}
                min={1}
                max={10}
                leftLabel={value.leftLabel || ""}
                rightLabel={value.rightLabel || ""}
                palette={palette}
                label={trait}
              />
              {value.notes && value.notesVisible !== false && (
                <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                  <p className="text-white/70 text-sm whitespace-pre-wrap">{value.notes}</p>
                </div>
              )}
            </div>
          );
        }
      }

      // If it's an object with a description property, use that
      if (value.description) {
        return value.description;
      }
      // Otherwise, stringify the object but format it for readability
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return "Complex object";
      }
    }

    // Use ScaleBar for 1-10 scale values (numeric or string numeric)
    const isScaleValue = (() => {
      let num = value;
      if (typeof value === "string") num = parseFloat(value);
      return typeof num === "number" && !isNaN(num) && num >= 1 && num <= 10;
    })();
    if (isScaleValue) {
      let num = value;
      if (typeof value === "string") num = parseFloat(value);
      const { leftLabel, rightLabel } = getScaleLabels(trait, value);
      // Check if value is an object with notes (from transformed data)
      const notes = typeof value === "object" && value !== null ? value.notes : null;
      const notesVisible = typeof value === "object" && value !== null ? value.notesVisible !== false : true;
      return (
        <div>
          <ScaleBar
            value={num}
            min={1}
            max={10}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
            palette={palette}
            label={trait}
            customTitle={questionTitles[trait]}
          />
          {notes && notesVisible && (
            <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-white/70 text-sm whitespace-pre-wrap">{notes}</p>
            </div>
          )}
        </div>
      );
    }

    if (typeof value === "number") {
      // For other numerical values, show a simple bar
      return (
        <div className="mt-2">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, Math.max(0, value * 10))}%`,
                backgroundColor: "var(--primary-color)",
              }}
            />
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span>0</span>
            <span>5</span>
            <span>10</span>
          </div>
        </div>
      );
    }

    // Default case: just return the string value
    return String(value);
  };

  // Format the explanation text if there is one
  const formatText = (text) => {
    if (!text) return ["No information available"];

    if (typeof text !== "string") {
      if (typeof text === "object") {
        try {
          return [JSON.stringify(text, null, 2)];
        } catch (e) {
          return ["Complex object"];
        }
      }
      return [String(text)];
    }

    // If text is shorter than 200 chars, don't split it
    if (text.length < 200) return [text];

    // Split by sentences for better paragraph breaks
    const sentences = text.split(/(?<=[.!?])\s+/);
    const paragraphs = [];
    let currentParagraph = "";

    sentences.forEach((sentence) => {
      if (currentParagraph.length + sentence.length > 200) {
        paragraphs.push(currentParagraph);
        currentParagraph = sentence;
      } else {
        currentParagraph += (currentParagraph ? " " : "") + sentence;
      }
    });

    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }

    return paragraphs;
  };

  // Get explanation if value is an object with an explanation property
  const hasExplanation =
    typeof value === "object" && value && value.explanation;
  const explanation = hasExplanation ? value.explanation : null;
  const explanationParagraphs = explanation ? formatText(explanation) : null;

  // For display in the card, if we have an object with explanation, use the object's value or description
  // Always ensure displayValue is a string to prevent React rendering errors
  const displayValue = (() => {
    if (typeof value === "object" && value !== null) {
      if (value.explanation) {
        return value.value || value.description || "See details";
      }
      // If it's a scale object, extract the value
      if (typeof value.value !== "undefined") {
        return String(value.value);
      }
      if (value.description) {
        return value.description;
      }
      try {
        return JSON.stringify(value);
      } catch (e) {
        return "Complex object";
      }
    }
    return String(value);
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      onClick={onToggle}
      className={`rounded-xl overflow-hidden backdrop-blur-sm border border-white/10 cursor-pointer transition-all duration-300 ${
        isExpanded ? "col-span-full" : ""
      }`}
      style={{
        background: gradient,
      }}
      whileHover={{
        scale: isExpanded ? 1 : 1.02,
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
      }}
    >
      <motion.div
        className="p-6"
        initial={false}
        animate={{
          padding: isExpanded ? "1.5rem" : "1.5rem",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <motion.div
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-white"
              style={{ backgroundColor: "var(--primary-color)" }}
              whileHover={{ scale: 1.1 }}
            >
              <Icon size={20} />
            </motion.div>
            <h3 className="text-xl font-bold text-white">{trait}</h3>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.3 }}
            className="text-white/60"
          >
            <ChevronRight size={20} />
          </motion.div>
        </div>

        <AnimatePresence>
          {!isExpanded && (
            <motion.div
              className="text-white/70"
              exit={{ opacity: 0, height: 0 }}
            >
              {renderValue()}
            </motion.div>
          )}

          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 space-y-4"
            >
              <div className="text-white/90">{renderValue()}</div>

              {explanation && (
                <>
                  <div className="h-px bg-white/10 my-4"></div>
                  <h4 className="text-md font-medium text-white/90">
                    Why this matters:
                  </h4>
                  {explanationParagraphs.map((paragraph, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="text-white/80 leading-relaxed"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

const DynamicBrandPersonalitySection = ({
  personality,
  scrollProgress,
  palette,
  profileId,
  backgroundImage,
  sectionIndex = 0,
  questionTitles = {},
  responseData,
  questionnaireData,
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Enhanced animation controls
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 0.9, 0.7]);

  // Determine if this section should have the background on the left or right
  const isLeftAligned = sectionIndex % 2 === 0;

  // Cards per slide for mobile view
  const CARDS_PER_SLIDE = 3;

  // Set up CSS Variables for colors
  useEffect(() => {
    if (palette) {
      document.documentElement.style.setProperty(
        "--primary-color",
        palette.primary
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        palette.secondary
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        palette.accent
      );

      // Helper function to convert hex to RGB for gradients
      const hexToRgb = (hex) => {
        // Remove # if present
        hex = hex.replace("#", "");

        // Convert 3-digit hex to 6-digits
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        // Extract r, g, b values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `${r}, ${g}, ${b}`;
      };

      // Set RGB values for CSS variables
      try {
        document.documentElement.style.setProperty(
          "--primary-color-rgb",
          hexToRgb(palette.primary)
        );
        document.documentElement.style.setProperty(
          "--secondary-color-rgb",
          hexToRgb(palette.secondary)
        );
        document.documentElement.style.setProperty(
          "--accent-color-rgb",
          hexToRgb(palette.accent)
        );
      } catch (e) {
        console.error("Error setting RGB color values:", e);
      }
    }
  }, [palette]);

  // Function to toggle explanation visibility
  const toggleCard = (index) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index);
  };

  // Define icons for different personality traits
  const icons = {
    Mission: Star,
    Vision: Eye,
    Description: MessageCircle,
    "Surprising Feedback": Sparkles,
    "Hidden Quality": Eye,
    "Magic Wand": Sparkles,
    "Biggest Strength": Award,
    "Biggest Misconception": Zap,
    "Cultural Trends": BarChart,
    "Celebrity Choice": User,
    "Core Values": Heart,
    "Brand Voice": Feather,
    "Brand Persona": Users,
    "Personality Traits": Lightbulb,
    // Default fallbacks
    Values: Heart,
    Tone: Feather,
    Character: User,
  };

  // Get personality traits with icons from the data
  const getPersonalityCards = () => {
    return Object.entries(personality).map(([trait, value], index) => {
      // Use displayTitle if available (for duplicate titles with question IDs)
      const displayTrait = (typeof value === "object" && value !== null && value.displayTitle) 
        ? value.displayTitle 
        : trait;
      
      // Find exact match first
      let Icon = icons[displayTrait];

      // If no exact match, try to find partial match
      if (!Icon) {
        const lowerTrait = displayTrait.toLowerCase();
        for (const [iconKey, iconComponent] of Object.entries(icons)) {
          if (lowerTrait.includes(iconKey.toLowerCase())) {
            Icon = iconComponent;
            break;
          }
        }
      }

      // Default to User icon if no match found
      Icon = Icon || User;

      return {
        trait: displayTrait, // Use display title for trait name
        value,
        icon: Icon,
        index,
      };
    });
  };

  const personalityCards = getPersonalityCards();

  // Group items into slides for mobile navigation
  const itemSlides = [];
  for (let i = 0; i < personalityCards.length; i += CARDS_PER_SLIDE) {
    itemSlides.push(personalityCards.slice(i, i + CARDS_PER_SLIDE));
  }

  const nextSlide = () => {
    setActiveSlide((prev) => (prev < itemSlides.length - 1 ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : itemSlides.length - 1));
  };

  return (
    <motion.section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center py-24 overflow-hidden"
      style={{
        opacity,
        backgroundColor: "rgba(5, 8, 15, 0.95)", // Much darker background
      }}
    >
      {/* Background image with transparency mask blending */}
      {backgroundImage && (
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <motion.div
            className={`absolute inset-0 h-full bg-center bg-cover bg-no-repeat`}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              opacity: bgOpacity,
              filter: "contrast(1.2) brightness(1.15)",
              mixBlendMode: "soft-light",
              width: "75%",
              left: isLeftAligned ? "0" : "auto",
              right: isLeftAligned ? "auto" : "0",
              // Apply transparency mask for seamless blending
              maskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
            }}
          />
        </div>
      )}

      {/* Content container with proper positioning */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 ${
          isLeftAligned ? "ml-auto" : "mr-auto"
        }`}
      >
        <motion.h2
          className="text-5xl md:text-7xl mb-16 font-bold text-white"
          style={{ textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)" }}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Brand Personality
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-white/60 max-w-3xl mb-12 text-xl"
        >
          The human traits and characteristics that define the brand's identity
          and character.
        </motion.p>

        {/* AI-Generated Narrative Section */}
        {profileId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center mb-6">
              <motion.div
                className="w-12 h-12 rounded-full flex items-center justify-center mr-4 text-white"
                style={{ backgroundColor: "var(--primary-color)" }}
                whileHover={{ scale: 1.1 }}
              >
                <MessageCircle size={24} />
              </motion.div>
              <h3 className="text-3xl font-bold text-white">
                Personality Analysis
              </h3>
            </div>

            <OriginalResponsesDisplay
              sectionType="brandPersonality"
              responseData={responseData}
              questionnaireData={questionnaireData}
              textColor="rgba(255, 255, 255, 0.9)"
              accentColor="var(--primary-color)"
            />
          </motion.div>
        )}

        {/* Main Content */}
        <div className="space-y-10">
          {/* Desktop View: Interactive Cards Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-min">
            {personalityCards.map((card, index) => (
              <PersonalityCard
                key={`personality-${index}`}
                trait={card.trait}
                value={card.value}
                icon={card.icon}
                index={index}
                isExpanded={expandedCardIndex === index}
                onToggle={() => toggleCard(index)}
                palette={palette}
                questionTitles={questionTitles}
              />
            ))}
          </div>

          {/* Mobile View: Interactive Cards Carousel */}
          <div className="md:hidden relative">
            <div className="flex justify-between items-center mb-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronLeft
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>

              <div className="text-white/60 text-sm">
                {itemSlides.length > 0
                  ? `${activeSlide + 1} / ${itemSlides.length}`
                  : "0 / 0"}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={nextSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronRight
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>
            </div>

            <AnimatePresence mode="wait">
              {itemSlides.length > 0 ? (
                <motion.div
                  key={activeSlide}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {itemSlides[activeSlide].map((card, index) => (
                    <PersonalityCard
                      key={index}
                      trait={card.trait}
                      value={card.value}
                      icon={card.icon}
                      index={index + activeSlide * CARDS_PER_SLIDE}
                      isExpanded={
                        expandedCardIndex ===
                        index + activeSlide * CARDS_PER_SLIDE
                      }
                      onToggle={() =>
                        toggleCard(index + activeSlide * CARDS_PER_SLIDE)
                      }
                      palette={palette}
                      questionTitles={questionTitles}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-8 text-center bg-white/5 rounded-lg border border-white/10"
                >
                  <p className="text-white/70">
                    No content is available to display.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination dots - only show if there are multiple slides */}
            {itemSlides.length > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                {itemSlides.map((_, index) => (
                  <motion.button
                    key={index}
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        activeSlide === index
                          ? "var(--primary-color)"
                          : "rgba(255, 255, 255, 0.3)",
                    }}
                    onClick={() => setActiveSlide(index)}
                    whileHover={{ scale: 1.3 }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default DynamicBrandPersonalitySection;
